import {
  IPurchaseOrder,
  IPurchaseOrderWithItems,
  IPurchaseOrderItem,
  IPurchaseOrderReceipt,
  IPurchaseOrderReceiptItem,
  PurchaseOrdersClient,
  PurchaseOrderStatus,
} from "../clients/PurchaseOrdersClient";
import { StockClient } from "@Clients/StockClient";
import { StockCommitmentsClient } from "@Clients/StockCommitmentsClient";
import { BackorderQueueClient } from "@Clients/BackorderQueueClient";
import { useStockStore } from "@stores/stockStore";
import { useStockCommitmentsStore } from "@stores/stockCommitmentsStore";
import { create } from "zustand";

type ReceivePurchaseOrderItemInput = {
  purchase_order_item_id: string;
  product_id: string;
  quantity_received: number;
  location_id: string;
};

export type PurchaseOrdersStoreState = {
  purchaseOrders: IPurchaseOrderWithItems[];
  loading: boolean;
  error: Error | null;
  getPurchaseOrdersByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  createPurchaseOrder: (
    purchaseOrder: Omit<
      IPurchaseOrder,
      "id" | "po_number" | "created_at" | "updated_at"
    >
  ) => Promise<IPurchaseOrder>;
  updatePurchaseOrder: (purchaseOrder: IPurchaseOrder) => Promise<void>;
  deletePurchaseOrder: (id: string) => Promise<void>;
  getPurchaseOrderById: (id: string) => Promise<IPurchaseOrderWithItems | null>;
  createPurchaseOrderItem: (
    purchaseOrderItem: Omit<
      IPurchaseOrderItem,
      "id" | "created_at" | "updated_at"
    >
  ) => Promise<IPurchaseOrderItem>;
  updatePurchaseOrderItem: (
    purchaseOrderItem: Omit<IPurchaseOrderItem, "created_at" | "updated_at">
  ) => Promise<void>;
  deletePurchaseOrderItem: (id: string) => Promise<void>;
  updatePurchaseOrderStatus: (
    purchaseOrderId: string,
    status: PurchaseOrderStatus,
    organizationId: string
  ) => Promise<void>;
  getPurchaseOrdersByStatus: (
    status: PurchaseOrderStatus,
    organizationId: string
  ) => Promise<IPurchaseOrder[]>;
  updatePurchaseOrderTotal: (purchaseOrderId: string) => Promise<void>;
  receivePurchaseOrderItems: (params: {
    purchaseOrderId: string;
    items: ReceivePurchaseOrderItemInput[];
    receivedBy: string;
    notes?: string | null;
    organizationId: string;
  }) => Promise<{
    receipt: IPurchaseOrderReceipt;
    receiptItems: IPurchaseOrderReceiptItem[];
  }>;
};

export const usePurchaseOrdersStore = create<PurchaseOrdersStoreState>(
  (set, get) => ({
    purchaseOrders: [],
    loading: true,
    error: null,

    async getPurchaseOrdersByOrganizationId(
      organization_id: string,
      useLoading = true
    ) {
      if (useLoading) {
        set({ loading: true });
      }
      try {
        // Fetch purchase orders with supplier data joined from database
        const purchaseOrders =
          await PurchaseOrdersClient.getPurchaseOrdersByOrganizationId(
            organization_id,
            true // includeSupplier = true
          );
        set({ purchaseOrders });
      } catch (error) {
        console.error("Error loading purchase orders:", error);
        set({ error: error as Error });
      } finally {
        if (useLoading) {
          set({ loading: false });
        }
      }
    },

    async createPurchaseOrder(
      purchaseOrder: Omit<
        IPurchaseOrder,
        "id" | "po_number" | "created_at" | "updated_at"
      >
    ): Promise<IPurchaseOrder> {
      set({ loading: true });
      try {
        // Set default status if not provided
        const purchaseOrderWithDefaults = {
          ...purchaseOrder,
          status: (purchaseOrder.status || "draft") as PurchaseOrderStatus,
        };

        const newPurchaseOrder =
          await PurchaseOrdersClient.createPurchaseOrder(
            purchaseOrderWithDefaults
          );
        set((state) => ({
          ...state,
          purchaseOrders: [
            { ...newPurchaseOrder, purchase_order_items: [] },
            ...state.purchaseOrders,
          ],
        }));
        return newPurchaseOrder;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    async updatePurchaseOrder(purchaseOrder: IPurchaseOrder) {
      set({ loading: true });
      try {
        const updatedPurchaseOrder =
          await PurchaseOrdersClient.updatePurchaseOrder(purchaseOrder);
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === updatedPurchaseOrder.id
              ? {
                ...updatedPurchaseOrder,
                purchase_order_items: po.purchase_order_items,
              }
              : po
          ),
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    async deletePurchaseOrder(id: string) {
      set({ loading: true });
      try {
        await PurchaseOrdersClient.deletePurchaseOrder(id);
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.filter((po) => po.id !== id),
        }));
      } catch (error) {
        set({ error: error as Error });
      } finally {
        set({ loading: false });
      }
    },

    async getPurchaseOrderById(
      id: string
    ): Promise<IPurchaseOrderWithItems | null> {
      try {
        // First check if we have it in the store
        const existingPurchaseOrder = get().purchaseOrders.find(
          (po) => po.id === id
        );
        if (existingPurchaseOrder) {
          return existingPurchaseOrder;
        }

        // If not, fetch from API with supplier data
        const purchaseOrder = await PurchaseOrdersClient.getPurchaseOrderById(
          id,
          true
        ); // includeSupplier = true
        set((state) => ({
          ...state,
          purchaseOrders: [
            purchaseOrder,
            ...state.purchaseOrders.filter((po) => po.id !== id),
          ],
        }));
        return purchaseOrder;
      } catch (error) {
        set({ error: error as Error });
        return null;
      }
    },

    async createPurchaseOrderItem(
      purchaseOrderItem: Omit<
        IPurchaseOrderItem,
        "id" | "created_at" | "updated_at"
      >
    ): Promise<IPurchaseOrderItem> {
      try {
        // Create purchase order item with basic information
        const newPurchaseOrderItem =
          await PurchaseOrdersClient.createPurchaseOrderItem({
            ...purchaseOrderItem,
            quantity_received: 0,
            status: "pending",
          });

        // Update the purchase order in the store to include the new item
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === purchaseOrderItem.purchase_order_id
              ? {
                ...po,
                purchase_order_items: [
                  ...(po.purchase_order_items || []),
                  newPurchaseOrderItem,
                ],
              }
              : po
          ),
        }));

        // Update the purchase order total
        await this.updatePurchaseOrderTotal(purchaseOrderItem.purchase_order_id);

        return newPurchaseOrderItem;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      }
    },

    async updatePurchaseOrderItem(
      purchaseOrderItem: Omit<IPurchaseOrderItem, "created_at" | "updated_at">
    ) {
      try {
        const updatedPurchaseOrderItem =
          await PurchaseOrdersClient.updatePurchaseOrderItem(purchaseOrderItem);

        // Update the purchase order in the store
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === purchaseOrderItem.purchase_order_id
              ? {
                ...po,
                purchase_order_items:
                  po.purchase_order_items?.map((item) =>
                    item.id === purchaseOrderItem.id
                      ? updatedPurchaseOrderItem
                      : item
                  ) || [],
              }
              : po
          ),
        }));

        // Update the purchase order total
        await this.updatePurchaseOrderTotal(purchaseOrderItem.purchase_order_id);
      } catch (error) {
        set({ error: error as Error });
      }
    },

    async deletePurchaseOrderItem(id: string) {
      try {
        await PurchaseOrdersClient.deletePurchaseOrderItem(id);

        // Remove the item from the store
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) => ({
            ...po,
            purchase_order_items:
              po.purchase_order_items?.filter((item) => item.id !== id) || [],
          })),
        }));

        // Update the purchase order total
        const purchaseOrder = get().purchaseOrders.find((po) =>
          po.purchase_order_items?.some((item) => item.id === id)
        );
        if (purchaseOrder) {
          await this.updatePurchaseOrderTotal(purchaseOrder.id);
        }
      } catch (error) {
        set({ error: error as Error });
      }
    },

    async updatePurchaseOrderStatus(
      purchaseOrderId: string,
      status: PurchaseOrderStatus,
      organizationId: string
    ) {
      try {
        set({ loading: true, error: null });

        // Update in database
        const updatedPurchaseOrder =
          await PurchaseOrdersClient.updatePurchaseOrderStatus(
            purchaseOrderId,
            status,
            organizationId
          );

        // Update in store
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === purchaseOrderId ? { ...po, ...updatedPurchaseOrder } : po
          ),
          loading: false,
        }));
      } catch (error) {
        console.error("Error updating purchase order status:", error);
        set({ error: error as Error, loading: false });
      }
    },

    async getPurchaseOrdersByStatus(
      status: PurchaseOrderStatus,
      organizationId: string
    ) {
      try {
        set({ loading: true, error: null });

        const purchaseOrders = await PurchaseOrdersClient.getPurchaseOrdersByStatus(
          status,
          organizationId
        );

        set({ loading: false });
        return purchaseOrders;
      } catch (error) {
        console.error("Error fetching purchase orders by status:", error);
        set({ error: error as Error, loading: false });
        return [];
      }
    },

    async updatePurchaseOrderTotal(purchaseOrderId: string) {
      try {
        const updatedPurchaseOrder =
          await PurchaseOrdersClient.updatePurchaseOrderTotal(purchaseOrderId);

        // Update in store
        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === purchaseOrderId
              ? { ...po, total_amount: updatedPurchaseOrder.total_amount }
              : po
          ),
        }));
      } catch (error) {
        console.error("Error updating purchase order total:", error);
        set({ error: error as Error });
      }
    },

    async receivePurchaseOrderItems({
      purchaseOrderId,
      items,
      receivedBy,
      notes,
      organizationId,
    }) {
      set({ loading: true, error: null });
      try {
        const receiptItemsInput = items.filter(
          (item) => item.quantity_received > 0
        );
        if (receiptItemsInput.length === 0) {
          throw new Error("No receipt quantities provided");
        }

        let purchaseOrder = get().purchaseOrders.find(
          (po) => po.id === purchaseOrderId
        );
        if (!purchaseOrder) {
          const fetched = await PurchaseOrdersClient.getPurchaseOrderById(
            purchaseOrderId,
            true
          );
          purchaseOrder = fetched as IPurchaseOrderWithItems;
          set((state) => ({
            ...state,
            purchaseOrders: [
              purchaseOrder as IPurchaseOrderWithItems,
              ...state.purchaseOrders.filter((po) => po.id !== purchaseOrderId),
            ],
          }));
        }

        if (!purchaseOrder) {
          throw new Error("Purchase order not found");
        }

        const receipt = await PurchaseOrdersClient.createPurchaseOrderReceipt({
          purchase_order_id: purchaseOrderId,
          received_by: receivedBy,
          received_at: new Date().toISOString(),
          notes: notes || null,
          organization_id: organizationId,
        });

        const receiptItemsPayload = receiptItemsInput.map((item) => ({
          receipt_id: receipt.id,
          purchase_order_item_id: item.purchase_order_item_id,
          product_id: item.product_id,
          quantity_received: item.quantity_received,
          location_id: item.location_id,
          organization_id: organizationId,
        }));

        const receiptItems =
          await PurchaseOrdersClient.createPurchaseOrderReceiptItems(
            receiptItemsPayload
          );

        const updatedItemsMap = new Map<string, IPurchaseOrderItem>();

        for (const item of receiptItemsInput) {
          const currentItem = purchaseOrder.purchase_order_items.find(
            (poItem) => poItem.id === item.purchase_order_item_id
          );
          if (!currentItem) {
            continue;
          }

          const newQuantityReceived =
            Number(currentItem.quantity_received || 0) +
            Number(item.quantity_received);
          const newStatus: IPurchaseOrderItem["status"] =
            newQuantityReceived >= currentItem.quantity_ordered
              ? "received"
              : "partial";

          const updatedItem = await PurchaseOrdersClient.updatePurchaseOrderItem({
            ...currentItem,
            quantity_received: newQuantityReceived,
            status: newStatus,
          });

          updatedItemsMap.set(updatedItem.id, updatedItem);
        }

        const updatedItems = purchaseOrder.purchase_order_items.map((item) => {
          const updated = updatedItemsMap.get(item.id);
          return updated ? { ...item, ...updated } : item;
        });

        const anyReceived = updatedItems.some(
          (item) => Number(item.quantity_received || 0) > 0
        );
        const allReceived = updatedItems.every(
          (item) =>
            Number(item.quantity_received || 0) >=
            Number(item.quantity_ordered || 0)
        );

        let updatedPurchaseOrderStatus: PurchaseOrderStatus | null = null;
        if (allReceived) {
          updatedPurchaseOrderStatus = "received";
        } else if (anyReceived) {
          updatedPurchaseOrderStatus = "partial";
        }

        if (updatedPurchaseOrderStatus) {
          const updatedPurchaseOrder =
            await PurchaseOrdersClient.updatePurchaseOrderStatus(
              purchaseOrderId,
              updatedPurchaseOrderStatus,
              organizationId
            );
          purchaseOrder = {
            ...purchaseOrder,
            ...updatedPurchaseOrder,
          };
        }

        set((state) => ({
          ...state,
          purchaseOrders: state.purchaseOrders.map((po) =>
            po.id === purchaseOrderId
              ? {
                ...po,
                ...purchaseOrder,
                purchase_order_items: updatedItems,
              }
              : po
          ),
        }));

        const stockCommitmentsStore = useStockCommitmentsStore.getState();
        const stockStore = useStockStore.getState();

        const plannedAllocations =
          await PurchaseOrdersClient.getPlannedAllocationsByPurchaseOrderId(
            purchaseOrderId
          );
        const plannedByItem = plannedAllocations.reduce<
          Record<string, typeof plannedAllocations>
        >((acc, allocation) => {
          if (!acc[allocation.purchase_order_item_id]) {
            acc[allocation.purchase_order_item_id] = [];
          }
          acc[allocation.purchase_order_item_id].push(allocation);
          return acc;
        }, {});

        const productIds = Array.from(
          new Set(receiptItemsInput.map((item) => item.product_id))
        );

        for (const item of receiptItemsInput) {
          const existingInventory =
            await StockClient.getInventoryByProductIdAndLocationId(
              item.product_id,
              item.location_id
            );
          if (existingInventory.length > 0) {
            const inventoryRecord = existingInventory[0];
            await StockClient.updateStock({
              id: inventoryRecord.id,
              product_id: inventoryRecord.product_id,
              location_id: inventoryRecord.location_id,
              quantity:
                Number(inventoryRecord.quantity) +
                Number(item.quantity_received),
              organization_id: organizationId,
              current_status_id: inventoryRecord.current_status_id || null,
              committed_quantity: inventoryRecord.committed_quantity || 0,
            });
          } else {
            await StockClient.createStock({
              product_id: item.product_id,
              location_id: item.location_id,
              quantity: Number(item.quantity_received),
              organization_id: organizationId,
              current_status_id: null,
              committed_quantity: 0,
            });
          }

          let remainingToAllocate = Number(item.quantity_received);
          const allocationsForItem =
            plannedByItem[item.purchase_order_item_id] || [];

          for (const allocation of allocationsForItem) {
            if (remainingToAllocate <= 0) break;
            const plannedQty = Number(allocation.quantity_planned || 0);
            if (plannedQty <= 0) continue;

            const allocateQty = Math.min(remainingToAllocate, plannedQty);
            await StockCommitmentsClient.commitStock(
              allocation.order_id,
              allocation.order_item_id,
              allocation.product_id,
              allocateQty,
              organizationId
            );

            const backordersForItem =
              await BackorderQueueClient.getBackorderQueueByOrderItemId(
                allocation.order_item_id
              );
            if (backordersForItem.length > 0) {
              const backorder = backordersForItem[0];
              const remainingNeeded = backorder.quantity_needed - allocateQty;
              await BackorderQueueClient.updateBackorderQueue({
                id: backorder.id,
                quantity_needed: Math.max(0, remainingNeeded),
                status: remainingNeeded <= 0 ? "fulfilled" : "pending",
              });
            }

            const remainingPlanned = plannedQty - allocateQty;
            if (remainingPlanned <= 0) {
              await PurchaseOrdersClient.deletePlannedAllocation(allocation.id);
            } else {
              await PurchaseOrdersClient.updatePlannedAllocation({
                id: allocation.id,
                quantity_planned: remainingPlanned,
              });
            }

            remainingToAllocate -= allocateQty;
          }
        }

        for (const productId of productIds) {
          await StockCommitmentsClient.processBackordersForProduct(productId);
        }

        for (const productId of productIds) {
          await stockStore.refreshStocksByProductId(productId);
        }
        await stockCommitmentsStore.getBulkStockAvailability(productIds);

        set({ loading: false });
        return { receipt, receiptItems };
      } catch (error) {
        set({ error: error as Error, loading: false });
        throw error;
      }
    },
  })
);
